import { ReactNode } from "react";
import { UserCog } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

/**
 * Il messaggio da mostrare quando una lista non ha righe.
 *
 * Serve perché per un operatore tecnico o di vigilanza "vuota" ha un
 * significato diverso: dal restringimento dello scope, vede solo il lavoro
 * assegnato a lui più quello non ancora assegnato nel suo centro. Se il suo
 * centro ha distribuito tutto ai colleghi, lui trova una lista vuota — e
 * "Nessun intervento." si legge come "il sistema non ne ha", cioè come un
 * guasto, invece che come "non ce n'è per te".
 *
 * Tre scelte di sostanza:
 *
 *  - **solo per gli operatori.** Per tutti gli altri il vuoto vuol dire
 *    davvero "non c'è niente", e il messaggio generico è quello giusto.
 *  - **non quando c'è un filtro attivo.** Lì il vuoto è spiegato dal filtro,
 *    che ha già il suo banner: dire "non hai lavori assegnati" a chi ha appena
 *    filtrato per uno stato sarebbe falso.
 *  - **nessun conteggio dei lavori dei colleghi.** Si potrebbe aggiungere "il
 *    tuo centro ne ha 12", ed è informazione utile, ma richiede una seconda
 *    query per ogni lista ed espone un numero su lavoro che l'operatore non
 *    deve vedere. Il messaggio spiega la regola: basta a non farla sembrare
 *    un'avaria.
 */
export function useMessaggioListaVuota(predefinito: string, filtroAttivo = false): ReactNode {
  const isOperatore = useIsOperatore();
  const organizzazione = useNomeOrganizzazione();
  if (!isOperatore || filtroAttivo) return predefinito;
  return <ListaVuotaOperatore organizzazione={organizzazione} />;
}

/**
 * "centro" o "istituto" secondo il ruolo. Un operatore di vigilanza non ha un
 * Centro Assistenza, e leggersi "chiedi al tuo centro" lo manderebbe a cercare
 * una cosa che non lo riguarda.
 */
export function useNomeOrganizzazione(): "centro" | "istituto" {
  const { user } = useAuth();
  return user?.role === "operatore_vigilanza" ? "istituto" : "centro";
}

/**
 * Per le pagine che non usano `Table` e devono decidere da sé se mostrare il
 * riquadro — il Calendario delle Installazioni, dove "vuoto" vuol dire nessuna
 * tessera da nessuna parte, non una tabella senza righe.
 *
 * Gate di sola COPIA, non di autorizzazione: qui si sceglie un testo, non cosa
 * l'utente può vedere — quello lo decide il backend, ed è l'unico posto dove
 * deve essere deciso.
 */
export function useIsOperatore(): boolean {
  const { user } = useAuth();
  return user?.role === "operatore_tecnico" || user?.role === "operatore_vigilanza";
}

/** Il riquadro vero e proprio, esposto per le pagine che non usano `Table`. */
export function ListaVuotaOperatore({ organizzazione = "centro" }: { organizzazione?: "centro" | "istituto" }) {
  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-2 py-4 text-center">
      <UserCog className="h-6 w-6 text-ink/30" aria-hidden="true" />
      <p className="text-sm font-semibold text-ink">Non hai lavori assegnati.</p>
      <p className="text-sm text-ink/50">
        Vedi quelli assegnati a te e quelli non ancora assegnati nel tuo {organizzazione}. Se qui è vuoto, sono
        già stati presi in carico: chiedi al tuo {organizzazione} di assegnartene uno.
      </p>
    </div>
  );
}
